import React, {
  createContext, useContext, useMemo, useState, useCallback, useEffect,
} from 'react';

/** Consent categories. `necessary` is always granted and cannot be disabled. */
export type CookieCategory = 'necessary' | 'maps' | 'analytics';
export interface CookiePrefs { maps: boolean; analytics: boolean; }

interface ConsentCtx {
  /** true once the visitor has made an explicit choice */
  decided: boolean;
  prefs: CookiePrefs;
  /** first-visit banner visible */
  bannerOpen: boolean;
  /** granular preferences modal visible */
  prefsOpen: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePrefs: (next: CookiePrefs) => void;
  openPreferences: () => void;
  /** close the preferences modal without changing anything (re-shows the banner if undecided) */
  closePreferences: () => void;
  closeBanner: () => void;
  hasConsent: (cat: CookieCategory) => boolean;
  /** alias kept for the floating button — opens the preferences modal */
  reopen: () => void;
}

const Ctx = createContext<ConsentCtx | null>(null);

const STORAGE_KEY = 'rts_cookie_consent_v2';
const OLD_KEY = 'rts_cookie_consent';
const DEFAULT_PREFS: CookiePrefs = { maps: false, analytics: false };

/** Read stored consent, migrating the previous single-choice key if present. */
function readStored(): { prefs: CookiePrefs; decided: boolean } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return { prefs: { maps: !!p.maps, analytics: !!p.analytics }, decided: true };
    }
    const old = localStorage.getItem(OLD_KEY);
    if (old === 'all') return { prefs: { maps: true, analytics: true }, decided: true };
    if (old === 'necessary') return { prefs: { maps: false, analytics: false }, decided: true };
  } catch { /* ignore (private mode / SSR / prerender) */ }
  return { prefs: DEFAULT_PREFS, decided: false };
}

function persist(prefs: CookiePrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prefs, ts: Date.now() }));
    localStorage.removeItem(OLD_KEY); // drop the legacy key once migrated
  } catch { /* ignore */ }
}

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [prefs, setPrefs] = useState<CookiePrefs>(() => readStored().prefs);
  const [decided, setDecided] = useState<boolean>(() => readStored().decided);
  const [bannerOpen, setBannerOpen] = useState<boolean>(() => !readStored().decided);
  const [prefsOpen, setPrefsOpen] = useState(false);

  const commit = useCallback((next: CookiePrefs) => {
    persist(next);
    setPrefs(next);
    setDecided(true);
    setBannerOpen(false);
    setPrefsOpen(false);
  }, []);

  const acceptAll = useCallback(() => commit({ maps: true, analytics: true }), [commit]);
  const rejectAll = useCallback(() => commit({ maps: false, analytics: false }), [commit]);
  const savePrefs = useCallback((next: CookiePrefs) => commit(next), [commit]);
  const openPreferences = useCallback(() => { setBannerOpen(false); setPrefsOpen(true); }, []);
  const closePreferences = useCallback(() => {
    setPrefsOpen(false);
    setBannerOpen((prev) => prev || !decided); // undecided → bring the banner back
  }, [decided]);
  const closeBanner = useCallback(() => setBannerOpen(false), []);
  const hasConsent = useCallback(
    (cat: CookieCategory) => (cat === 'necessary' ? true : !!prefs[cat]),
    [prefs],
  );
  const reopen = openPreferences;

  // Placeholder: initialise analytics once the visitor grants the 'analytics' category.
  useEffect(() => {
    if (decided && prefs.analytics) {
      // When analytics consent is granted, initialise analytics here (e.g. GA / Matomo).
      // No analytics tool is configured yet — this is intentionally a no-op placeholder.
    }
  }, [decided, prefs.analytics]);

  const value = useMemo<ConsentCtx>(() => ({
    decided, prefs, bannerOpen, prefsOpen,
    acceptAll, rejectAll, savePrefs, openPreferences, closePreferences, closeBanner, hasConsent, reopen,
  }), [decided, prefs, bannerOpen, prefsOpen, acceptAll, rejectAll, savePrefs, openPreferences, closePreferences, closeBanner, hasConsent, reopen]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useCookieConsent(): ConsentCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCookieConsent must be used within CookieConsentProvider');
  return ctx;
}
